import {
  CommonModule,
  isPlatformBrowser,
  Location,
  NgOptimizedImage,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EnvironmentInjector,
  inject,
  runInInjectionContext,
  signal,
  ViewChild,
} from '@angular/core';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { SignalRService } from '../../../core/services/signal-r.service';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { AuthService } from '../../../core/services/auth.service';
import { ObjectLiteral } from '../../../core/models/ObjectLiteral';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { CountdownNoformattingService } from '../../../core/services/countdown-noformatting.service';
import { auditTime, map, takeUntil } from 'rxjs';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { PaymentService } from '../../../core/services/payment.service';
import { errorToastAction } from '../../../core/store/actions/toast.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../../../core/store';
import { MakePaymentTspComponent } from '../../paymentSummary/make-payment-tsp/make-payment-tsp.component';
import { Unsub } from '../../../core/services/unsub.class';
import { JSONUtils } from '../../../core/utils/JSONUtils';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { PathnameService } from '../../../core/services/pathname.service';
import { ModalV1Component } from '../../../shared/widgets/modal-v1/modal-v1.component';

@Component({
  selector: 'app-payment-redirect-tsp',
  standalone: true,
  imports: [
    NzModalModule,
    CommonModule,
    NgOptimizedImage,
    NzProgressModule,
    MakePaymentTspComponent,
    RouterModule,
    ModalV1Component,
  ],
  templateUrl: './payment-redirect-tsp.component.html',
  styleUrl: './payment-redirect-tsp.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentRedirectTspComponent extends Unsub {
  billRef = signal('');
  isAnonymous = injectQueryParams('isAnonymous');
  isMdaUser = injectQueryParams('isMdaUser');
  txRefSignal = injectQueryParams('txRef');
  txnAmount = injectQueryParams('txnAmount');
  signalRService = inject(SignalRService);
  paymentService = inject(PaymentService);
  pathnameService = inject(PathnameService);
  cdr = inject(ChangeDetectorRef);

  activatedRoute = inject(ActivatedRoute);
  location = inject(Location);
  isConnected = signal<boolean>(false);
  openModal = signal<boolean>(true);

  retries = signal<number>(0);
  failedConnectionRetries = signal<number>(0);
  paymentData = signal<ObjectLiteral | null>(null);
  socketResponse = signal<ObjectLiteral | null>(null);
  store = inject(Store<AppState>);
  //   countDownService = inject(CountdownService);
  countDownNoFormattingService = inject(CountdownNoformattingService);
  private envInjector = inject(EnvironmentInjector);
  isBrowser: boolean;
  redirectUrl: string = '';
  authService = inject(AuthService);
  isProviderMissing = signal<boolean>(false);
  isSuccess = signal<boolean>(false);
  formatOne = (percent: number): string => {
    const val = percent / 10;
    return `${val} ${val <= 1 ? 'sec' : 'secs'}`;
  };

  @ViewChild(ModalV1Component) modal: ModalV1Component;

  isPaymentConfirmationError = computed(() => {
    const gateway = this.paymentData()?.provider;
    const data = this.mutation.data();
    const isSuccess = this.isSuccess();
    const noProvider = this.isProviderMissing();
    switch (gateway) {
      case 'interswitch':
        return (!!data && !isSuccess) || this.mutation.isError();
      case 'remita':
        return (!!data && !isSuccess) || this.mutation.isError();
    }
    return noProvider || false;
  });

  get providerImageSrc() {
    return this.paymentData()?.provider === 'interswitch'
      ? 'assets/images/payment-gateways/interswitch_confirmation_logo.png'
      : this.paymentData()?.provider === 'remita'
        ? 'assets/images/remita_logo_payment_redirect_page.png'
        : 'assets/images/primeguage_logo_payment_redirect_page.png';
  }

  get routeLink() {
    const pathname = this.pathnameService.pathname;

    switch (true) {
      case pathname.startsWith('/admin'):
        return '/admin';

      case pathname.startsWith('/mda'):
        return '/mda';

      case pathname.startsWith('/expert-system'):
        return '/expert-system';

      case pathname.startsWith('/user'):
        return '/user/dashboard';

      default:
        return '/';
    }
  }

  get parseInteger() {
    return parseInt;
  }

  progressPercent$ = this.countDownNoFormattingService.displaySeconds$.pipe(
    auditTime(500),
    map((seconds) => {
      return (seconds / 10) * 100;
    }),
    takeUntil(this.unsubscribe$),
  );

  mutation = injectMutation(() => ({
    mutationKey: ['paykaduna-payment-confirmation'],
    mutationFn: (
      params: ObjectLiteral & { gateway: 'interswitch' | 'remita' },
    ) => {
      return this.paymentService.getPaymentStatus(params);
    },
    onSuccess: (data) => {
      const isSuccessful = ['00', '10', 10, '11', 11].includes(
        data?.responseCode,
      );
      if (!isSuccessful) {
        this.store.dispatch(
          errorToastAction({
            autohide: true,
            delay: 7000,
            placement: 'top-end',
            message: data?.responseDescription || 'Unable to confirm payment',
          }),
        );

        this.isSuccess.set(false);
        return;
      }

      // Trigger success countdown
      this.isSuccess.set(true);
      this.countDownNoFormattingService.startCountdown(10, true);
    },
    onError: (error: any) => {
      console.log('error :>> ', error);
      this.store.dispatch(
        errorToastAction({
          autohide: true,
          delay: 4000,
          placement: 'top-end',
          message: 'Unable to confirm payment',
        }),
      );

      if (this.signalRService) {
        console.log('Signal R connection is closed');
        this.signalRService.stopConnection();
      }
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
    },
  }));

  constructor() {
    super();
    runInInjectionContext(this.envInjector, () => {
      this.isBrowser = typeof window !== 'undefined';
    });
  }

  ngAfterViewInit(): void {
    //Open dialog after view is initialized
    if (!!this.isBrowser) {
      this.paymentData.set(
        JSONUtils.parse(sessionStorage?.getItem('payment-data')),
      );
      this.redirectUrl = sessionStorage?.getItem('redirect-url') || '';
      this.modal?.openModal();

      this.redirectUrl = sessionStorage?.getItem('redirect-url') || '';
    }
  }

  ngOnInit(): void {
    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((paramMap) => {
        this.billRef.set(
          paramMap.get('ref') ||
            paramMap.get('billReference') ||
            paramMap.get('billRef') ||
            '',
        );
      });

    this.countDownNoFormattingService
      .watchCountDown()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isComplete: boolean) => {
        if (!isComplete) return;

        if (!!this.isSuccess()) {
          this.cdr.markForCheck();
          this.modal?.closeModal();
          if (!!this.redirectUrl) {
            window.location.href = this.redirectUrl;
          }
          return;
        }
        if (
          this.retries() >= 6 ||
          (!!this.socketResponse() && !this.isSuccess())
        ) {
          console.log('Limit reached!!!!');
          switch (this.paymentData()?.provider) {
            case 'interswitch':
              //Fetch interswitch payment confirmation manually
              this.mutation.mutate({
                BillReference: this.billRef(),
                AmountInKobo: this.paymentData().amount || 0,
                TransactionReference: this.paymentData().txRef,
                gateway: 'interswitch',
              });
              break;

            // To be updated
            case 'remita':
              this.mutation.mutate({
                rrr: this.paymentData().rrr || 0,
                gateway: 'remita',
              });
              //Fetch remita payment confirmation manually

              break;

            default:
              if (this.retries() >= 6) {
                if (this.signalRService) {
                  console.log('Signal R connection is closed');
                  this.signalRService.stopConnection();
                }
                this.unsubscribe$.next();
                this.unsubscribe$.complete();
                this.isProviderMissing.set(true);
              }
              break;
          }

          return;
        }
        if (!this.socketResponse()) {
          console.log('Socket retry ' + this.retries());
          const userData = this.authService.userData();

          this.signalRService.sendMessage('PaymentNotificationRequest', {
            userId: userData?.userId,
            billReference: this.billRef(),
          });

          this.countDownNoFormattingService.startCountdown(10, true);
        }

        this.retries.update((currNum) => currNum + 1);
      });

    //Start socket connection
    this.signalRService.startConnectionAnonymous();

    //Socket response when available
    this.signalRService.addMessageListener(
      'PaymentNotificationResponse',
      (data: ObjectLiteral) => {
        this.socketResponse.set(data);

        if (![400, 200].includes(data.statusCode)) {
          this.isSuccess.set(false);
          return;
        }
        this.countDownNoFormattingService.startCountdown(10, true);
        this.isSuccess.set(true);
      },
    );

    // Listen for successful socket connection
    this.signalRService
      .watchConnection()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isConnected) => {
        this.isConnected.set(isConnected);

        if (!!isConnected && !!this.billRef()) {
          const userData = this.authService.userData();

          // Trigger socket
          this.signalRService.sendMessage('PaymentNotificationRequest', {
            userId: userData?.userId,
            billReference: this.billRef(),
          });

          this.countDownNoFormattingService.startCountdown(5, true);
        }
      });

    // Listen for failed socket connection
    this.signalRService
      .watchConnectionError()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isError) => {
        if (isError) {
          if (this.failedConnectionRetries() >= 4) {
            console.log('Failed connection limit reached!!');
            this.retries.set(6);

            //Trigger countdown which triggers manual api call for payment confirmation
            this.countDownNoFormattingService.startCountdown(5, true);
            return;
          }
          this.failedConnectionRetries.update((curr) => curr + 1);
          this.signalRService.startConnectionAnonymous();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.signalRService) {
      console.log('Signal R connection is closed');
      this.signalRService.stopConnection();
    }

    if (this.isBrowser) {
      sessionStorage?.removeItem('payment-data');
    }
    this.openModal.set(false);
    this.modal?.closeModal();
  }
}
